import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClimbingRoute } from '../models/climbing/ClimbingRoute';
import { ClimbPerformance, getGradeAsString, GradeSystem } from '../models/climbing/Grade';
import { recordClimb, getFilteredClimbs } from '../services/firebase/climbs';
import { getClimbingRoute } from '../services/firebase/climbingRoute';
import { AvailableColors } from '../models/climbing/RouteColors';
import { useAuth } from '../contexts/AuthContext';
import CloseButton from '../components/CloseButton';
import '../styles/App.css';
import '../styles/RouteDetail.css'; // Use RouteDetail layout

const LogClimbPage: React.FC = () => {
  const { gymId, routeId } = useParams<{ gymId: string; routeId: string }>();
  const navigate = useNavigate();
  const { currentUser, refreshClimberProfile } = useAuth();
  
  const [route, setRoute] = useState<ClimbingRoute | null>(null);
  const [performance, setPerformance] = useState<ClimbPerformance>(ClimbPerformance.ON_SIGHT);
  const [hangs, setHangs] = useState<number>(0);
  const [attempt, setAttempt] = useState<number>(1);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [reachedTop, setReachedTop] = useState<boolean>(true);
  const [climbDate, setClimbDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [isTopped, setIsTopped] = useState<boolean>(true);

  // Fetch route details
  useEffect(() => {
    const fetchRoute = async () => {
      if (!gymId || !routeId) return;
      try {
        const routeData = await getClimbingRoute(gymId, routeId);
        if (routeData) {
          setRoute(routeData);
        } else {
          setError('Route not found');
        }
      } catch (err) {
        console.error('Error fetching route:', err);
        setError('Failed to load route data');
      }
    };
    fetchRoute();
  }, [gymId, routeId]);

  // Fetch previous attempts
  useEffect(() => {
    const fetchPreviousAttempts = async () => {
      if (!routeId || !gymId || !currentUser) return;
      try {
        setLoading(true);
        const previousClimbs = await getFilteredClimbs(
          currentUser.uid, 
          { routeId, gymId },
          1000
        );
        setAttempt(previousClimbs.length + 1);
      } catch (error) {
        console.error('Error fetching previous attempts:', error);
        setAttempt(1);
      } finally {
        setLoading(false);
      }
    };
    
    if (route) {
      fetchPreviousAttempts();
    }
  }, [route?.id, routeId, currentUser, gymId]);

  useEffect(() => {
    if (!isTopped) {
      setPerformance(ClimbPerformance.NOT_TOPPED);
      setReachedTop(true);
      if (hangs < 1) {
        setHangs(1);
      }
    } else {
      if (attempt === 1) {
        setPerformance(ClimbPerformance.ON_SIGHT);
      } else {
        setPerformance(ClimbPerformance.REDPOINT);
      }
      setHangs(0);
      setReachedTop(true);
    }
  }, [isTopped, attempt]);

  const getColorHex = (colorValue?: string): string => {
    if (!colorValue) return '#cccccc';
    const color = AvailableColors.find(c => c.value === colorValue);
    return color ? color.hexCode : '#cccccc';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !gymId || !route) return;

    setIsSubmitting(true);
    setError('');

    try {
      const dateTimestamp = new Date(climbDate).setHours(0, 0, 0, 0);
      
      await recordClimb(
        currentUser.uid,
        gymId,
        route,
        performance,
        notes,
        hangs,
        attempt,
        reachedTop,
        dateTimestamp
      );
      
      if (refreshClimberProfile) {
        await refreshClimberProfile();
      }
      
      // Navigate back to the gym routes overflow
      navigate(`/gyms/${gymId}`);
    } catch (err: any) {
      console.error('Error logging climb:', err);
      setError(err.message || 'Failed to log climb. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  if (!gymId || !routeId || !currentUser) {
    return <div className="route-detail-container"><p>Loading...</p></div>;
  }

  return (
    <div className="route-detail-container log-climb-page">
      <div className="route-detail-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <CloseButton onClose={handleClose} />
        <h1 style={{ flex: 1, textAlign: 'center' }}>Log Your Climb</h1>
        <div style={{ width: '48px' }}></div> {/* Spacer for centering matching CloseButton width */}
      </div>

      <div className="page-content width-match-route-detail">
        {route ? (
          <div className="route-summary" style={{ padding: '16px', background: '#f5f5f5', borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
            <div className="route-color" style={{ 
              backgroundColor: getColorHex(route.color),
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              marginRight: '10px'
            }}></div>
            <div className="route-info">
              <div className="route-grade">
                <strong>Grade:</strong> {route.grade !== undefined 
                  ? getGradeAsString(route.grade, GradeSystem.FRENCH) 
                  : 'Not graded'}
              </div>
              {route.style && <div className="route-style"><strong>Style:</strong> {route.style}</div>}
              {route.location && <div className="route-location"><strong>Location:</strong> {route.location}</div>}
            </div>
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="form-container">
          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="climbDate">Date of Climb</label>
            <div className="date-picker-container" style={{ marginTop: '8px' }}>
              <input
                id="climbDate"
                type="date"
                value={climbDate}
                onChange={(e) => setClimbDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
              />
              <div className="current-date-display" style={{ marginTop: '8px', color: '#666', fontSize: '0.9em' }}>
                {new Date(climbDate).toLocaleDateString('en-US', { 
                  weekday: 'short', 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric' 
                })}
              </div>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label>Performance</label>
            <div className="performance-chips" style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button 
                type="button"
                className={`chip ${isTopped ? 'selected' : ''}`}
                style={{ 
                  flex: 1, 
                  padding: '10px 4px', 
                  borderRadius: '20px', 
                  border: isTopped ? '2px solid #4CAF50' : '1px solid #ddd', 
                  backgroundColor: isTopped ? '#4CAF50' : 'white', 
                  color: isTopped ? 'white' : 'inherit',
                  fontWeight: isTopped ? 'bold' : 'normal',
                  fontSize: '0.9em'
                }}
                onClick={() => setIsTopped(true)}
              >
                Topped
              </button>
              <button 
                type="button"
                className={`chip ${!isTopped ? 'selected' : ''}`}
                style={{ 
                  flex: 1, 
                  padding: '10px 4px', 
                  borderRadius: '20px', 
                  border: !isTopped ? '2px solid #4CAF50' : '1px solid #ddd', 
                  backgroundColor: !isTopped ? '#4CAF50' : 'white', 
                  color: !isTopped ? 'white' : 'inherit',
                  fontWeight: !isTopped ? 'bold' : 'normal',
                  fontSize: '0.9em'
                }}
                onClick={() => setIsTopped(false)}
              >
                Attempted
              </button>
            </div>
          </div>

          {!isTopped && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="hangs">Number of Hangs/Rests</label>
              <div className="number-picker" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                <button 
                  type="button" 
                  className="number-picker-btn" 
                  style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid #ccc', fontSize: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'white', cursor: 'pointer' }}
                  onClick={() => setHangs(Math.max(1, hangs - 1))}
                  disabled={hangs <= 1}
                >
                  -
                </button>
                <div className="number-picker-value" style={{ fontSize: '1.2em', fontWeight: 'bold' }}>{hangs}</div>
                <button 
                  type="button" 
                  className="number-picker-btn"
                  style={{ width: '40px', height: '40px', borderRadius: '50%', border: '1px solid #ccc', fontSize: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'white', cursor: 'pointer' }}
                  onClick={() => setHangs(hangs + 1)}
                >
                  +
                </button>
              </div>
            </div>
          )}

          {!isTopped && (
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="reachedTop">Did you reach the top?</label>
              <div className="checkbox-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <input
                  id="reachedTop"
                  type="checkbox"
                  checked={reachedTop}
                  onChange={(e) => setReachedTop(e.target.checked)}
                  style={{ width: '20px', height: '20px' }}
                />
                <label htmlFor="reachedTop" className="checkbox-label" style={{ margin: 0 }}>Yes, I reached the top</label>
              </div>
            </div>
          )}

          <div className="form-group" style={{ marginBottom: '20px' }}>
            <label htmlFor="notes">Notes (optional)</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any comments about your climb..."
              rows={4}
              style={{ width: '100%', padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '16px', marginTop: '8px', boxSizing: 'border-box' }}
            />
          </div>

          {error && <div className="error-message" style={{ color: 'var(--error-color)', marginTop: '16px' }}>{error}</div>}

          <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px', marginBottom: '40px' }}>
            <button
              type="submit"
              className="button primary"
              disabled={isSubmitting}
              style={{ padding: '14px 32px', fontSize: '1.1em' }}
            >
              {isSubmitting ? 'Logging...' : 'Log Climb'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogClimbPage;